import { toCanvas } from 'html-to-image';

import {
  SOCIAL_SHARE_OG_ASPECT_RATIO,
  SOCIAL_SHARE_OG_HEIGHT,
  SOCIAL_SHARE_OG_WIDTH,
} from 'src/helpers/socialShareConstants';
import { compressImageForUpload } from 'src/helpers/compressImageForUpload';

interface CoverCropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/** Center-crop source to the target aspect ratio (OG 1.91:1). */
export function computeOgCoverCrop(
  srcWidth: number,
  srcHeight: number,
  dstWidth = SOCIAL_SHARE_OG_WIDTH,
  dstHeight = SOCIAL_SHARE_OG_HEIGHT,
): CoverCropRect {
  const srcAspect = srcWidth / srcHeight;
  const dstAspect = dstWidth / dstHeight;

  if (Math.abs(srcAspect - dstAspect) < 0.001) {
    return { sx: 0, sy: 0, sw: srcWidth, sh: srcHeight };
  }

  if (srcAspect > dstAspect) {
    const sw = srcHeight * dstAspect;
    return { sx: (srcWidth - sw) / 2, sy: 0, sw, sh: srcHeight };
  }

  const sh = srcWidth / dstAspect;
  return { sx: 0, sy: (srcHeight - sh) / 2, sw: srcWidth, sh };
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to encode OG image'));
        }
      },
      'image/jpeg',
      0.85,
    );
  });
}

function normalizeCanvasToOgSize(source: HTMLCanvasElement): HTMLCanvasElement {
  const output = document.createElement('canvas');
  output.width = SOCIAL_SHARE_OG_WIDTH;
  output.height = SOCIAL_SHARE_OG_HEIGHT;

  const ctx = output.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to create OG image canvas');
  }

  const crop = computeOgCoverCrop(source.width, source.height);
  ctx.drawImage(
    source,
    crop.sx,
    crop.sy,
    crop.sw,
    crop.sh,
    0,
    0,
    SOCIAL_SHARE_OG_WIDTH,
    SOCIAL_SHARE_OG_HEIGHT,
  );

  return output;
}

function prepareElementForOgCapture(element: HTMLElement) {
  const previousWidth = element.style.width;
  const previousHeight = element.style.height;
  const previousMaxWidth = element.style.maxWidth;
  const previousTransform = element.style.transform;
  const previousOverflow = element.style.overflow;

  const scaledParent = element.parentElement;
  const previousParentWidth = scaledParent?.style.width ?? '';
  const previousParentHeight = scaledParent?.style.height ?? '';
  const previousParentOverflow = scaledParent?.style.overflow ?? '';

  element.style.width = `${SOCIAL_SHARE_OG_WIDTH}px`;
  element.style.height = `${SOCIAL_SHARE_OG_HEIGHT}px`;
  element.style.maxWidth = 'none';
  element.style.transform = 'none';
  element.style.overflow = 'hidden';

  if (scaledParent) {
    scaledParent.style.width = `${SOCIAL_SHARE_OG_WIDTH}px`;
    scaledParent.style.height = `${SOCIAL_SHARE_OG_HEIGHT}px`;
    scaledParent.style.overflow = 'hidden';
  }

  return () => {
    element.style.width = previousWidth;
    element.style.height = previousHeight;
    element.style.maxWidth = previousMaxWidth;
    element.style.transform = previousTransform;
    element.style.overflow = previousOverflow;

    if (scaledParent) {
      scaledParent.style.width = previousParentWidth;
      scaledParent.style.height = previousParentHeight;
      scaledParent.style.overflow = previousParentOverflow;
    }
  };
}

export async function bakeSocialSharePreview(element: HTMLElement): Promise<File> {
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const restore = prepareElementForOgCapture(element);

  try {
    const sourceCanvas = await toCanvas(element, {
      width: SOCIAL_SHARE_OG_WIDTH,
      height: SOCIAL_SHARE_OG_HEIGHT,
      pixelRatio: 1,
      cacheBust: true,
      style: {
        width: `${SOCIAL_SHARE_OG_WIDTH}px`,
        height: `${SOCIAL_SHARE_OG_HEIGHT}px`,
        transform: 'none',
        aspectRatio: `${SOCIAL_SHARE_OG_ASPECT_RATIO}`,
      },
    });

    const outputCanvas = normalizeCanvasToOgSize(sourceCanvas);
    const blob = await canvasToBlob(outputCanvas);
    const baked = new File([blob], 'social-share.jpg', { type: 'image/jpeg' });
    return compressImageForUpload(baked, 'og');
  } finally {
    restore();
  }
}
